import { useEffect, useState } from 'react';

/**
 * Lightweight, SDK-free lookup of the admin-managed site logo.
 *
 * The Supabase JS client is deliberately NOT used here: Navigation and Footer
 * are part of the initial bundle, and pulling the SDK into that chain would
 * hurt LCP. Instead we hit the public REST endpoint once, after window load,
 * and fall back to the bundled logo until (and unless) a custom one exists.
 */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
const CACHE_KEY = 'dd_site_logo_url';

let cached: string | null | undefined;
let inFlight: Promise<string | null> | null = null;

function readSessionCache(): string | null | undefined {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (raw === null) return undefined;
    return raw === '' ? null : raw;
  } catch {
    return undefined;
  }
}

function writeSessionCache(value: string | null) {
  try {
    sessionStorage.setItem(CACHE_KEY, value ?? '');
  } catch {
    /* storage unavailable — ignore */
  }
}

async function fetchLogoUrl(): Promise<string | null> {
  if (cached !== undefined) return cached;
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const res = await fetch(
        `${SUPABASE_URL}/rest/v1/site_settings?key=eq.site_logo_url&select=value`,
        {
          headers: {
            apikey: SUPABASE_KEY,
            Authorization: `Bearer ${SUPABASE_KEY}`,
            Accept: 'application/json',
          },
        }
      );
      if (!res.ok) throw new Error('lookup failed');
      const rows = (await res.json()) as Array<{ value: string | null }>;
      const value = rows?.[0]?.value?.trim() || null;
      cached = value;
      writeSessionCache(value);
      return value;
    } catch {
      cached = null;
      return null;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

/**
 * Returns the custom logo URL when one has been uploaded in the admin panel,
 * otherwise the provided fallback (the bundled logo asset).
 */
export function useSiteLogo(fallback: string): string {
  const [url, setUrl] = useState<string>(() => {
    if (cached !== undefined) return cached ?? fallback;
    const session = readSessionCache();
    if (session !== undefined) {
      cached = session;
      return session ?? fallback;
    }
    return fallback;
  });

  useEffect(() => {
    if (cached !== undefined) {
      setUrl(cached ?? fallback);
      return;
    }

    let active = true;
    const run = () => {
      fetchLogoUrl().then((value) => {
        if (active) setUrl(value ?? fallback);
      });
    };

    // Defer until after the page has loaded so the logo lookup never competes
    // with the critical rendering path.
    if (document.readyState === 'complete') {
      const id = window.setTimeout(run, 0);
      return () => {
        active = false;
        window.clearTimeout(id);
      };
    }

    window.addEventListener('load', run, { once: true });
    return () => {
      active = false;
      window.removeEventListener('load', run);
    };
  }, [fallback]);

  return url;
}

/** Clears the cached value so a freshly uploaded logo appears immediately. */
export function clearSiteLogoCache() {
  cached = undefined;
  try {
    sessionStorage.removeItem(CACHE_KEY);
  } catch {
    /* ignore */
  }
}
